import { Button, Checkbox, DatePicker, Input, message, Popconfirm, Space, Spin, Table, Tag } from "antd";
import { CheckOutlined, CloseOutlined, FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { serverFetch } from "@server";
import { fmtDate, strDate2Server } from "@extensions/convert";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "../orders/order.css";
import "./license-orders.css";
import LicenseOrder from "./license-order";

const LicenseOrders = () => {
  const getSavedFilter = () => {
    try {
      const saved = localStorage.getItem("licenseOrders");
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          dateBeg: parsed.dateBeg ? dayjs(parsed.dateBeg) : dayjs(),
          dateEnd: parsed.dateEnd ? dayjs(parsed.dateEnd) : dayjs(),
          allCompanies: parsed.allCompanies || false,
        };
      }
    } catch (e) {
      console.error("Error loading saved filter:", e);
    }
    return {
      dateBeg: dayjs(),
      dateEnd: dayjs(),
      allCompanies: false,
    };
  };

  const isMobile = useViewportBreakpoint() === "mobile";
  const [filter, setFilter] = useState(getSavedFilter);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const user = useSelector(s => s.user);

  const [filterOpen, setFilterOpen] = useState(false);
  const [draftDateBeg, setDraftDateBeg] = useState(null);
  const [draftDateEnd, setDraftDateEnd] = useState(null);
  const [draftAllCompanies, setDraftAllCompanies] = useState(false);

  const fetchLicenseOrders = f => {
    setLoading(true);
    let url = `license/orders?dateBeg=${strDate2Server(f.dateBeg)}&dateEnd=${strDate2Server(f.dateEnd)}&allCompanies=${f.allCompanies}`;
    if (searchText.trim()) {
      url += `&searchText=${encodeURIComponent(searchText.trim())}`;
    }
    serverFetch(url)
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const getData = () => fetchLicenseOrders(filter);

  useEffect(() => {
    fetchLicenseOrders(getSavedFilter());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- первичная загрузка
  }, []);

  const filterSummary = useMemo(() => {
    const parts = [];
    if (filter.dateBeg && filter.dateEnd) {
      parts.push(`${filter.dateBeg.format("DD.MM.YYYY")} — ${filter.dateEnd.format("DD.MM.YYYY")}`);
    } else if (filter.dateBeg) parts.push(`с ${filter.dateBeg.format("DD.MM.YYYY")}`);
    else if (filter.dateEnd) parts.push(`по ${filter.dateEnd.format("DD.MM.YYYY")}`);
    else parts.push("период не задан");
    if (user?.admin) {
      parts.push(filter.allCompanies ? "все компании" : "только своя компания");
    }
    const q = searchText.trim();
    if (q) parts.push(`поиск: ${q.length > 40 ? `${q.slice(0, 40)}…` : q}`);
    return parts.join(" · ");
  }, [filter, searchText, user?.admin]);

  const openFilterModal = () => {
    setDraftDateBeg(filter.dateBeg);
    setDraftDateEnd(filter.dateEnd);
    setDraftAllCompanies(filter.allCompanies);
    setFilterOpen(true);
  };

  const applyFilter = () => {
    if (!draftDateBeg || !draftDateEnd) {
      message.warning("Укажите период дат");
      return;
    }
    const next = {
      ...filter,
      dateBeg: draftDateBeg,
      dateEnd: draftDateEnd,
      ...(user?.admin ? { allCompanies: draftAllCompanies } : {}),
    };
    setFilter(next);
    try {
      localStorage.setItem(
        "licenseOrders",
        JSON.stringify({
          dateBeg: next.dateBeg?.format("YYYY-MM-DD"),
          dateEnd: next.dateEnd?.format("YYYY-MM-DD"),
          allCompanies: next.allCompanies,
        })
      );
    } catch (e) {
      console.error("Error saving filter:", e);
    }
    setFilterOpen(false);
    fetchLicenseOrders(next);
  };

  const cancelFilter = () => setFilterOpen(false);

  const formatSummNum = v => (v != null && v !== "" ? Number(v) : null);

  /** Сумма: `1 234 567` — пробелы по разрядам, без дробной части */
  const fmtSummAmount = v => {
    const n = formatSummNum(v);
    if (n == null || Number.isNaN(n)) return null;
    const rounded = Math.round(n);
    const neg = rounded < 0;
    const intDigits = String(Math.abs(rounded));
    const spaced = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${neg ? "-" : ""}${spaced}`;
  };

  const handlePay = async record => {
    try {
      setLoading(true);
      const response = await serverFetch("license/payOrder", {
        method: "POST",
        bodyData: record,
      });

      setLoading(false);

      if (response && response.url) {
        window.open(response.url, "_blank");
      } else {
        message.error("Не получен URL для оплаты");
      }
    } catch (e) {
      setLoading(false);
      message.error(e.userMessage || "Ошибка при инициации оплаты");
    }
  };

  const handleMarkPaid = async record => {
    try {
      setLoading(true);
      await serverFetch("license/markPaid", {
        method: "POST",
        bodyData: record,
      });
      setLoading(false);
      message.success("Заказ помечен как оплаченный, ключи сгенерированы");
      getData();
    } catch (e) {
      setLoading(false);
      message.error(e.userMessage || "Ошибка при пометке заказа оплаченным");
    }
  };

  const renderPayButton = record => {
    if (user?.admin) {
      return (
        <span onClick={e => e.stopPropagation()}>
          <Popconfirm
            title="Подтверждение"
            description="Вы действительно хотите пометить заказ как оплаченный?"
            onConfirm={() => handleMarkPaid(record)}
            okText="Да"
            cancelText="Отмена"
          >
            <Button danger size="small">
              Оплатить
            </Button>
          </Popconfirm>
        </span>
      );
    }
    return (
      <Button
        type="primary"
        size="small"
        onClick={e => {
          e.stopPropagation();
          handlePay(record);
        }}
      >
        Оплатить
      </Button>
    );
  };

  const columns = [
    { dataIndex: "accId", title: "#", width: 60 },
    ...(user?.admin ? [{ dataIndex: "companyName", title: "Компания", width: 200 }] : []),
    { dataIndex: "createDate", title: "Дата", render: fmtDate },
    { dataIndex: "progName", title: "Программа" },
    { dataIndex: "licenseName", title: "Лицензия" },
    { dataIndex: "licDuration", title: "Срок" },
    { dataIndex: "qty", title: "Кол." },
    { dataIndex: "summ", title: "Сумма", render: v => fmtSummAmount(v) ?? "—" },
    {
      dataIndex: "payDate",
      title: "Оплачен",
      render: (value, record) => {
        if (!value) {
          return renderPayButton(record);
        }
        if (record.receipt) {
          return (
            <Tag
              color="green"
              style={{ cursor: "pointer" }}
              onClick={e => {
                e.stopPropagation();
                window.open(record.receipt, "_blank");
              }}
            >
              {fmtDate(value)}
            </Tag>
          );
        }
        return fmtDate(value);
      },
    },
    { dataIndex: "komment", title: "Комментарий" },
  ];

  const onFilterChange = value => {
    const newFilter = { ...filter, ...value };
    setFilter(newFilter);
    try {
      const toSave = {
        dateBeg: newFilter.dateBeg?.format("YYYY-MM-DD"),
        dateEnd: newFilter.dateEnd?.format("YYYY-MM-DD"),
        allCompanies: newFilter.allCompanies,
      };
      localStorage.setItem("licenseOrders", JSON.stringify(toSave));
    } catch (e) {
      console.error("Error saving filter:", e);
    }
  };

  const handleSearchKeyDown = e => {
    if (e.key === "Enter") {
      getData();
    }
  };

  const onRowClick = record => {
    setSelected(record.accId);
    setSelectedCompanyId(record.companyId);
  };

  const handleModalClose = refresh => {
    setSelected(null);
    setSelectedCompanyId(null);
    refresh && getData();
  };

  const renderPayMobile = record => {
    const value = record.payDate;
    if (!value) {
      return renderPayButton(record);
    }
    if (record.receipt) {
      return (
        <Tag
          color="green"
          style={{ cursor: "pointer" }}
          onClick={e => {
            e.stopPropagation();
            window.open(record.receipt, "_blank");
          }}
        >
          {fmtDate(value)}
        </Tag>
      );
    }
    return <span>{fmtDate(value)}</span>;
  };

  const mobileListEl = (
    <Spin spinning={loading}>
      <div className="license-orders-mobile-list">
        {items.map(record => {
          const line2 = [record.progName, record.licenseName, record.licDuration].filter(
            v => v != null && String(v).trim() !== ""
          );
          let line2Text = line2.length ? line2.join(" · ") : "—";
          const qtyNum = Number(record.qty);
          if (Number.isFinite(qtyNum) && qtyNum !== 1) {
            line2Text = line2.length ? `${line2Text} · ×${qtyNum}` : `×${qtyNum}`;
          }
          const comment = record.komment != null ? String(record.komment).trim() : "";
          const summFormatted = fmtSummAmount(record.summ);
          const showCommentRow = Boolean(comment) || summFormatted != null;
          return (
            <div
              key={`${record.companyId}-${record.accId}`}
              role="button"
              tabIndex={0}
              className="license-orders-mobile-card"
              onClick={() => onRowClick(record)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onRowClick(record);
                }
              }}
            >
              <div className="license-orders-mobile-row1">
                <span>{fmtDate(record.createDate) || "—"}</span>
                {user?.admin ? (
                  <span className="license-orders-mobile-row1-company">{record.companyName || "—"}</span>
                ) : null}
                <span className="license-orders-mobile-row1-id">#{record.accId}</span>
              </div>
              <div className="license-orders-mobile-row23">
                <div className="license-orders-mobile-meta-right">
                  <div className="license-orders-mobile-meta-line2">{line2Text}</div>
                </div>
                <div className="license-orders-mobile-pay">
                  {summFormatted != null ? (
                    <span className="license-orders-mobile-row23-sum">{summFormatted}</span>
                  ) : (
                    renderPayMobile(record)
                  )}
                </div>
              </div>
              {showCommentRow ? (
                <div className="license-orders-mobile-row4">
                  {comment ? <span className="license-orders-mobile-row4-comment">{comment}</span> : null}
                  {summFormatted != null ? (
                    <span className="license-orders-mobile-row4-pay">{renderPayMobile(record)}</span>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </Spin>
  );

  const tableEl = (
    <Table
      columns={columns}
      dataSource={items}
      loading={loading}
      rowKey={record => `${record.companyId}-${record.accId}`}
      pagination={false}
      size="small"
      onRow={record => ({
        onClick: () => onRowClick(record),
        style: { cursor: "pointer" },
      })}
    />
  );

  return (
    <div
      className={
        isMobile ? "page orders-page--mobile license-orders-page--mobile" : "page license-orders-page--desktop"
      }
    >
      {isMobile ? (
        <>
          <div className="license-orders-mobile-body">
            <div className="orders-mobile-toolbar">
              <div className="company-backups-mobile-search-row">
                <Input
                  placeholder="Поиск по телефону, почте, наименованию, ИНН"
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  allowClear
                  className="company-backups-mobile-search-input"
                />
              </div>
              <div className="company-backups-mobile-filter-row">
                <span className="company-backups-mobile-filter-summary" title={filterSummary}>
                  {filterSummary}
                </span>
                <Button
                  type="default"
                  icon={<FilterOutlined />}
                  onClick={openFilterModal}
                  aria-label="Фильтр"
                  className="company-backups-mobile-filter-icon-btn"
                />
                <Button
                  type="default"
                  icon={<ReloadOutlined />}
                  onClick={getData}
                  loading={loading}
                  disabled={!filter.dateBeg || !filter.dateEnd}
                  aria-label="Обновить"
                  className="company-backups-mobile-refresh-btn"
                />
              </div>
            </div>
            <div className="license-orders-mobile-new-row">
              <Button
                type="primary"
                block
                onClick={() => {
                  setSelected(0);
                  setSelectedCompanyId(user?.companyId);
                }}
                disabled={loading}
              >
                Новый
              </Button>
            </div>
            <div className="orders-mobile-scroll license-orders-mobile-scroll">{mobileListEl}</div>
          </div>
          {filterOpen && (
            <div
              className="misoft-modal-overlay company-backups-filter-overlay--top"
              onClick={e => e.target === e.currentTarget && cancelFilter()}
            >
              <div
                className="misoft-modal-content company-backups-filter-sheet--top"
                onClick={e => e.stopPropagation()}
              >
                <div className="company-base-sheet-header">
                  <span className="company-base-sheet-title">Фильтр</span>
                  <Space size="small" className="company-base-sheet-actions">
                    <Button
                      type="primary"
                      icon={<CheckOutlined />}
                      onClick={applyFilter}
                      aria-label="Применить"
                      className="company-base-header-icon-btn"
                    />
                    <Button
                      type="default"
                      icon={<CloseOutlined />}
                      onClick={cancelFilter}
                      aria-label="Отмена"
                      className="company-base-header-icon-btn"
                    />
                  </Space>
                </div>
                <div className="company-base-sheet-body company-backups-filter-sheet-body">
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">С даты</span>
                    <DatePicker
                      value={draftDateBeg}
                      onChange={setDraftDateBeg}
                      format="DD.MM.YYYY"
                      allowClear={false}
                      style={{ width: "100%" }}
                    />
                  </div>
                  <div className="company-backups-filter-field">
                    <span className="company-backups-filter-label">По дату</span>
                    <DatePicker
                      value={draftDateEnd}
                      onChange={setDraftDateEnd}
                      format="DD.MM.YYYY"
                      allowClear={false}
                      style={{ width: "100%" }}
                    />
                  </div>
                  {user?.admin ? (
                    <div className="company-backups-filter-field company-backups-filter-switch-row">
                      <span>По всем компаниям</span>
                      <Checkbox checked={draftAllCompanies} onChange={e => setDraftAllCompanies(e.target.checked)} />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="license-orders-desktop-toolbar">
            <Space wrap>
              <div>Период с</div>
              <DatePicker
                format="DD.MM.YYYY"
                value={filter.dateBeg}
                onChange={e => onFilterChange({ dateBeg: e })}
                allowClear={false}
              />
              <div>по</div>
              <DatePicker
                format="DD.MM.YYYY"
                value={filter.dateEnd}
                onChange={e => onFilterChange({ dateEnd: e })}
                allowClear={false}
              />
              {user?.admin && (
                <Checkbox checked={filter.allCompanies} onChange={e => onFilterChange({ allCompanies: e.target.checked })}>
                  По всем компаниям
                </Checkbox>
              )}
              <Input
                placeholder="Поиск по телефону, почте, наименованию, ИНН"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                style={{ width: 400 }}
              />
              <Button type="primary" onClick={getData} loading={loading}>
                Обновить
              </Button>
              <Button
                onClick={() => {
                  setSelected(0);
                  setSelectedCompanyId(user?.companyId);
                }}
                disabled={loading}
              >
                Новый
              </Button>
            </Space>
          </div>
          <div className="license-orders-desktop-scroll">{tableEl}</div>
        </>
      )}
      {selected != null && selectedCompanyId != null && (
        <LicenseOrder companyId={selectedCompanyId} accId={selected} onClose={handleModalClose} />
      )}
    </div>
  );
};

export default LicenseOrders;
